import {
  Search,
  Bell,
  HelpCircle,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useAuth } from '@/features/auth/useAuth';
import { formatPlan, useEntitlement } from '@/lib/entitlements';
import { BrandLogo } from './BrandLogo';

interface TopbarProps {
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

/**
 * Top navigation bar. Matches the RxBB Figma "Home" top nav (node 11050:1544):
 * 68px tall, translucent dark glass (rgba(11,18,32,0.68) + blur), bottom hairline
 * border. Left: sidebar toggle + brand logo + global "Search with Orbis" field.
 * Right: toolbar icon buttons, a divider, and the user avatar.
 *
 * The sidebar toggle is only rendered when its state + handler are passed in —
 * keeps this component free of any global store and lets the shell decide
 * whether collapse is available (e.g. it's a no-op on mobile where the sidebar
 * is always hidden).
 */
export function Topbar({ sidebarCollapsed, onToggleSidebar }: TopbarProps = {}) {
  const { user, signOut } = useAuth();
  const entitlement = useEntitlement();

  return (
    <header className="flex h-[68px] shrink-0 items-center justify-between border-b border-[#1F2937] bg-[rgba(11,18,32,0.68)] pl-3 pr-8 backdrop-blur-md">
      <div className="flex items-center gap-6 lg:gap-8">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={!sidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden size-10 place-items-center rounded-lg text-white/75 transition-colors hover:bg-white/5 hover:text-white lg:grid"
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={20} strokeWidth={1.75} />
            ) : (
              <PanelLeftClose size={20} strokeWidth={1.75} />
            )}
          </button>
        )}
        <BrandLogo />
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-4">
        {entitlement && (
          <span
            className="hidden rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-xs font-medium text-brand-orange md:inline"
            title="Azure Marketplace plan"
          >
            {formatPlan(entitlement.internalPlan)} · {entitlement.licensedQuantity}{' '}
            {entitlement.licensedQuantity === 1 ? 'seat' : 'seats'}
          </span>
        )}
        <IconButton label="Notifications" Icon={Bell} />
        <IconButton label="Help" Icon={HelpCircle} />
        <IconButton label="Settings" Icon={Settings} />
        <IconButton label="Sign out" Icon={LogOut} onClick={() => void signOut()} />
        <span className="h-6 w-px bg-[#7C98B6]/40" />
        <Avatar name={user?.name ?? 'Guest'} />
      </div>
    </header>
  );
}

function GlobalSearch() {
  return (
    <label className="hidden h-10 w-[360px] items-center gap-2 rounded-lg border border-[#374151] bg-[#020617] px-5 md:flex xl:w-[467px]">
      <Search size={16} className="shrink-0 text-[#70707B]" />
      <input
        type="search"
        placeholder="Search with Orbis"
        className="min-w-0 flex-1 appearance-none border-0 bg-transparent text-[15px] font-light text-white outline-none placeholder:text-[#70707B]"
      />
    </label>
  );
}

function IconButton({
  label,
  Icon,
  onClick,
}: {
  label: string;
  Icon: typeof Bell;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="grid h-11 w-11 place-items-center rounded-lg text-white/80 transition-colors hover:bg-white/5 hover:text-white"
    >
      <Icon size={22} strokeWidth={1.75} />
    </button>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-brand-navy-700 to-tertiary-500 text-sm font-semibold text-white ring-2 ring-white/10"
      title={name}
    >
      {initials || 'U'}
    </div>
  );
}
